import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  repositoryArchitectureBibleApi,
  type ApplicationLayout,
  type BackendCapability,
  type BibleDashboard,
  type BuildTool,
  type DependencyRule,
  type DocumentationArea,
  type EnvironmentProfile,
  type MonorepoArea,
  type PhilosophyPrinciple,
  type RABBibleStatus,
  type SharedPackage,
  type TestingLayout,
  type BibleValidation,
} from '../../api/repository-architecture-bible';

type Tab = 'overview' | 'monorepo' | 'applications' | 'backend' | 'packages' | 'dependencies' | 'tooling' | 'environments' | 'testing' | 'docs' | 'status';
type Theme = 'light' | 'dark';

const statusColors: Record<string, string> = {
  active: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  planned: 'bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300',
};

export function RepositoryArchitectureBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<BibleDashboard | null>(null);
  const [philosophy, setPhilosophy] = useState<PhilosophyPrinciple[]>([]);
  const [monorepo, setMonorepo] = useState<MonorepoArea[]>([]);
  const [applications, setApplications] = useState<ApplicationLayout[]>([]);
  const [capabilities, setCapabilities] = useState<BackendCapability[]>([]);
  const [packages, setPackages] = useState<SharedPackage[]>([]);
  const [dependencies, setDependencies] = useState<DependencyRule[]>([]);
  const [buildTools, setBuildTools] = useState<BuildTool[]>([]);
  const [environments, setEnvironments] = useState<EnvironmentProfile[]>([]);
  const [testing, setTesting] = useState<TestingLayout[]>([]);
  const [documentation, setDocumentation] = useState<DocumentationArea[]>([]);
  const [status, setStatus] = useState<RABBibleStatus | null>(null);
  const [validation, setValidation] = useState<BibleValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, phil, mono, apps, caps, pkgs, deps, tools, envs, test, docs, st] = await Promise.all([
        repositoryArchitectureBibleApi.dashboard(),
        repositoryArchitectureBibleApi.philosophy(),
        repositoryArchitectureBibleApi.monorepoStructure(),
        repositoryArchitectureBibleApi.applications(),
        repositoryArchitectureBibleApi.backendCapabilities(),
        repositoryArchitectureBibleApi.sharedPackages(),
        repositoryArchitectureBibleApi.dependencyRules(),
        repositoryArchitectureBibleApi.buildTooling(),
        repositoryArchitectureBibleApi.environments(),
        repositoryArchitectureBibleApi.testingLayout(),
        repositoryArchitectureBibleApi.documentation(),
        repositoryArchitectureBibleApi.status(),
      ]);
      setDashboard(dash);
      setPhilosophy(phil);
      setMonorepo(mono);
      setApplications(apps);
      setCapabilities(caps);
      setPackages(pkgs);
      setDependencies(deps);
      setBuildTools(tools);
      setEnvironments(envs);
      setTesting(test);
      setDocumentation(docs);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load repository architecture bible');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateBible = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await repositoryArchitectureBibleApi.validate({
        philosophy_defined: true, monorepo_structure_defined: true, applications_defined: true,
        backend_capabilities_defined: true, shared_packages_defined: true, dependency_rules_defined: true,
        build_tooling_defined: true, environments_defined: true, testing_layout_defined: true,
        documentation_defined: true, acceptance_passed: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Repository architecture bible passes all acceptance criteria' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-zinc-800/50 bg-zinc-900' : 'border-zinc-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-zinc-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-zinc-50/40 text-zinc-900';
  const accent = dark ? 'text-sky-300' : 'text-sky-700';
  const tabActive = 'bg-sky-700 text-white dark:bg-sky-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'monorepo', label: 'Monorepo' },
    { id: 'applications', label: 'Applications' },
    { id: 'backend', label: 'Backend' },
    { id: 'packages', label: 'Packages' },
    { id: 'dependencies', label: 'Dependencies' },
    { id: 'tooling', label: 'Build & Tooling' },
    { id: 'environments', label: 'Environments' },
    { id: 'testing', label: 'Testing' },
    { id: 'docs', label: 'Documentation' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>RAB-000001</p>
          <h1 className="text-2xl font-semibold">Repository Architecture Bible</h1>
          <p className={`text-sm ${muted}`}>
            Enterprise monorepo structure, package organization, module boundaries, dependency rules and long-term codebase governance
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/repository-blueprint" className={`${accent} hover:underline`}>ICP-000002</Link>
            <span className={muted}>→</span>
            <Link to="/platform/architecture-decision-records" className={`${accent} hover:underline`}>ADR-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/engineering-execution-bible" className={`${accent} hover:underline`}>EEB-000001</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Decade-Scalable Monorepo</h2>
            <p className={`mt-1 text-sm ${muted}`}>Applies to: {dashboard.applies_to.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              The repository SHALL remain understandable, maintainable and scalable for at least the next decade.
              Every new capability SHALL fit into the repository without requiring structural redesign.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Monorepo Areas', value: dashboard.monorepo_areas },
              { label: 'Applications', value: dashboard.applications },
              { label: 'Backend Capabilities', value: dashboard.backend_capabilities },
              { label: 'Shared Packages', value: dashboard.shared_packages },
              { label: 'Dependency Rules', value: dashboard.dependency_rules },
              { label: 'Build Tools', value: dashboard.build_tools },
              { label: 'Environments', value: dashboard.environments },
              { label: 'Testing Layouts', value: dashboard.testing_layouts },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Repository Philosophy (§2)</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {philosophy.map((p) => (
                <div key={p.key} className="rounded-lg border p-3 text-sm dark:border-zinc-700">
                  <span className={`mr-2 rounded px-1.5 py-0.5 text-xs ${p.priority === 'P0' ? 'bg-sky-700 text-white dark:bg-sky-600' : 'bg-zinc-200 dark:bg-zinc-700'}`}>{p.priority}</span>
                  <strong>{p.name}</strong>
                  {p.description && <p className={`mt-1 ${muted}`}>{p.description}</p>}
                </div>
              ))}
            </div>
          </div>
          <button type="button" disabled={busy} onClick={validateBible} className="rounded-lg bg-sky-700 px-4 py-2 text-sm text-white hover:bg-sky-600 disabled:opacity-50 dark:bg-sky-600">
            Validate Repository Structure
          </button>
          {validation && (
            <div className={`rounded-xl border p-4 ${validation.valid ? 'border-emerald-300 dark:border-emerald-800' : 'border-amber-300 dark:border-amber-800'} ${card}`}>
              <p className="font-semibold">{validation.valid ? 'Structure Valid' : 'Validation Gaps Detected'}</p>
              {validation.gaps && validation.gaps.length > 0 && (
                <ul className={`mt-2 list-disc pl-5 text-sm ${muted}`}>{validation.gaps.map((g) => <li key={g}>{g}</li>)}</ul>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'monorepo' && (
        <div className={`rounded-xl border ${card}`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${dark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <th className="p-3 text-left">Path</th><th className="p-3 text-left">Purpose</th><th className="p-3 text-left">Owner</th><th className="p-3 text-left">Required</th>
            </tr></thead>
            <tbody>
              {monorepo.map((a) => (
                <tr key={a.key} className={`border-b ${dark ? 'border-zinc-800/50' : 'border-zinc-100'}`}>
                  <td className="p-3 font-mono text-xs">{a.path}</td>
                  <td className="p-3">{a.purpose}</td>
                  <td className={`p-3 ${muted}`}>{a.owner}</td>
                  <td className="p-3">{a.mandatory ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'applications' && (
        <div className="grid gap-3 sm:grid-cols-2">
          {applications.map((a) => (
            <div key={a.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{a.name}</span>
                <span className={`rounded px-2 py-0.5 text-xs ${statusColors[a.status] ?? statusColors.planned}`}>{a.status}</span>
              </div>
              <p className={`mt-1 font-mono text-xs ${muted}`}>{a.path}</p>
              {a.description && <p className={`mt-2 text-sm ${muted}`}>{a.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'backend' && (
        <div className="space-y-2">
          {capabilities.map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{c.name}</span>
                <span className={`rounded px-2 py-0.5 text-xs ${statusColors[c.status] ?? statusColors.planned}`}>{c.status}</span>
              </div>
              <p className={`mt-1 text-xs ${muted}`}>{c.service_path} · {c.pkg_path}</p>
              <p className={`mt-1 text-xs ${muted}`}>Owns: {c.owns?.join(', ')}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'packages' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {packages.map((p) => (
            <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{p.name}</p>
              <p className={`font-mono text-xs ${muted}`}>{p.path}</p>
              <span className="mt-2 inline-block rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">{p.category}</span>
              {p.description && <p className={`mt-2 text-sm ${muted}`}>{p.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'dependencies' && (
        <div className="space-y-2">
          {dependencies.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-mono text-xs">{d.from_layer}</span>
                <span className={muted}>→</span>
                <span className="font-mono text-xs">{d.to_layer}</span>
                <span className={`rounded px-2 py-0.5 text-xs ${d.allowed ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                  {d.allowed ? 'Allowed' : 'Forbidden'}
                </span>
              </div>
              {d.description && <p className={`mt-1 text-sm ${muted}`}>{d.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'tooling' && (
        <div className={`rounded-xl border ${card}`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${dark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <th className="p-3 text-left">Tool</th><th className="p-3 text-left">Stage</th><th className="p-3 text-left">Description</th><th className="p-3 text-left">Blocking</th>
            </tr></thead>
            <tbody>
              {buildTools.map((t) => (
                <tr key={t.key} className={`border-b ${dark ? 'border-zinc-800/50' : 'border-zinc-100'}`}>
                  <td className="p-3 font-medium">{t.name}{t.tool && <span className={`ml-1 font-mono text-xs ${muted}`}>({t.tool})</span>}</td>
                  <td className={`p-3 ${muted}`}>{t.stage}</td>
                  <td className={`p-3 ${muted}`}>{t.description}</td>
                  <td className="p-3">{t.blocking ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'environments' && (
        <div className="grid gap-3 sm:grid-cols-2">
          {environments.map((e) => (
            <div key={e.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{e.name}</p>
              <p className={`mt-1 text-sm ${muted}`}>{e.purpose}</p>
              {e.config_path && <p className={`mt-1 font-mono text-xs ${muted}`}>{e.config_path}</p>}
              {e.properties && (
                <ul className={`mt-2 list-disc pl-5 text-xs ${muted}`}>{e.properties.map((p) => <li key={p}>{p}</li>)}</ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'testing' && (
        <div className="space-y-2">
          {testing.map((t) => (
            <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{t.name}</span>
                <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">{t.test_type}</span>
              </div>
              <p className={`mt-1 font-mono text-xs ${muted}`}>{t.location}</p>
              {t.description && <p className={`mt-1 text-sm ${muted}`}>{t.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'docs' && (
        <div className="grid gap-3 sm:grid-cols-2">
          {documentation.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{d.name}</p>
              <p className={`font-mono text-xs ${muted}`}>{d.path}</p>
              {d.description && <p className={`mt-2 text-sm ${muted}`}>{d.description}</p>}
              {d.audience && <p className={`mt-1 text-xs ${muted}`}>Audience: {d.audience}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-5 ${card}`}>
          <h3 className="font-semibold">Bible Status</h3>
          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            {Object.entries(status).filter(([k]) => !['applies_to'].includes(k)).map(([k, v]) => (
              <div key={k}>
                <dt className={`text-xs uppercase ${muted}`}>{k.replace(/_/g, ' ')}</dt>
                <dd className="mt-0.5 font-medium">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</dd>
              </div>
            ))}
          </dl>
          <p className={`mt-4 text-sm ${muted}`}>Applies to: {status.applies_to.join(' · ')}</p>
        </div>
      )}
    </div>
  );
}
